import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { DashboardSummary, SuccessRate } from '@/api/types';
import { Banner, Card, ErrorNote, Money, Reference, Spinner, Timestamp } from '@/components/Common';
import { LevelPill, StatusPill } from '@/components/StatusPill';
import { money, percent } from '@/lib/format';

export function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['summary'],
    queryFn: () => api.get<DashboardSummary>('/api/admin/metrics/summary'),
    // The recent-orders feed is meant to feel live (spec §13).
    refetchInterval: 10_000,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const exposure = Object.entries(data.paidUndelivered);

  return (
    <div className="space-y-5">
      {data.exceptions.reconcilerHalted && (
        <Banner tone="danger">
          <strong>Reconciliation is halted.</strong> A burst of reversals tripped the anomaly
          breaker, so unknown orders are staying unknown until someone confirms the query endpoint
          is healthy. Restart it from the exceptions page once you have.
        </Banner>
      )}

      {data.exceptions.needingReview > 0 && (
        <Banner tone="danger">
          <strong>{data.exceptions.needingReview}</strong> order
          {data.exceptions.needingReview === 1 ? '' : 's'} need manual review.{' '}
          <Link to="/exceptions" className="underline">Open the queue</Link>
        </Banner>
      )}

      {exposure.length > 0 && (
        <Banner tone="warn">
          <strong>Paid, undelivered:</strong>{' '}
          {exposure.map(([currency, amount]) => `${currency} ${money(amount)}`).join(' · ')}
          <span className="ml-2 text-amber-800">
            — money taken with nothing proven delivered. This should be near zero at close.
          </span>
        </Banner>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.wallets.map((wallet) => (
          <div
            key={wallet.accountTypeId}
            className={`card p-4 ${
              wallet.level === 'CRITICAL' ? 'border-red-400'
                : wallet.level === 'WARN' ? 'border-amber-400' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="label">{wallet.name}</span>
              <LevelPill level={wallet.level} />
            </div>
            <div className="tabular mt-2 text-2xl font-semibold text-ink-900">
              {money(wallet.balance)}
            </div>
            <div className="mt-1 text-xs text-ink-500">
              as at <Timestamp value={wallet.capturedAt} />
            </div>
          </div>
        ))}
        {data.wallets.length === 0 && (
          <div className="card p-4 text-sm text-ink-500">
            No balance poll has landed yet.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <SuccessCard title="Success rate — last hour" rate={data.successRate1h} />
        <SuccessCard title="Success rate — last 24 hours" rate={data.successRate24h} />
        <Card title="Open work">
          <dl className="divide-y divide-ink-100">
            <Row label="Exception cases" value={data.exceptions.openCases}
                 to="/exceptions" alarm={data.exceptions.openCases > 0} />
            <Row label="Unknown outcomes" value={data.exceptions.unknown}
                 to="/orders?status=UNKNOWN" alarm={data.exceptions.unknown > 0} />
            <Row label="Open reservations" value={data.openReservations} to="/reservations" />
          </dl>
        </Card>
      </div>

      <Card title="Today by product category">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-ink-200">
            <tr>
              <th className="th">Category</th>
              <th className="th">Currency</th>
              <th className="th text-right">Orders</th>
              <th className="th text-right">Succeeded</th>
              <th className="th text-right">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {data.today.map((row) => (
              <tr key={`${row.category}-${row.currency}`}>
                <td className="td font-medium">{row.category}</td>
                <td className="td text-ink-500">{row.currency}</td>
                <td className="td tabular text-right">{row.orderCount}</td>
                <td className="td tabular text-right">{row.succeeded}</td>
                <td className="td tabular text-right">
                  <Money amount={money(row.totalValue)} currency={row.currency} />
                </td>
              </tr>
            ))}
            {data.today.length === 0 && (
              <tr><td className="td text-ink-500" colSpan={5}>Nothing has traded today yet.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>

      <Card title="Last 20 orders">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-ink-200">
            <tr>
              <th className="th">Reference</th>
              <th className="th">Status</th>
              <th className="th">Target</th>
              <th className="th text-right">Amount</th>
              <th className="th">Placed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {data.recentOrders.map((order) => (
              <tr key={order.id} className="hover:bg-ink-50">
                <td className="td">
                  <Link to={`/orders/${order.id}`} className="hover:underline">
                    <Reference value={order.agentReference} />
                  </Link>
                </td>
                <td className="td"><StatusPill status={order.status} /></td>
                <td className="td mono">{order.target}</td>
                <td className="td text-right">
                  <Money amount={money(order.amount)} currency={order.currency} />
                </td>
                <td className="td"><Timestamp value={order.createdAt} /></td>
              </tr>
            ))}
            {data.recentOrders.length === 0 && (
              <tr><td className="td text-ink-500" colSpan={5}>No orders yet.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}

function SuccessCard({ title, rate }: { title: string; rate: SuccessRate }) {
  // Jackson drops a null "rate" map entry entirely rather than sending JSON null (0 decided
  // orders), so this arrives as undefined, not null — == catches both.
  const value = rate.rate == null ? null : Number(rate.rate);
  const tone = value === null ? 'text-ink-400'
    : value >= 0.95 ? 'text-emerald-700'
    : value >= 0.85 ? 'text-amber-700' : 'text-red-700';
  return (
    <Card title={title}>
      <div className="p-4">
        <div className={`tabular text-3xl font-semibold ${tone}`}>{percent(rate.rate)}</div>
        <p className="mt-2 text-xs text-ink-500">
          {rate.succeeded} of {rate.decided} decided
          {rate.unknown > 0 && (
            <span className="text-amber-700"> · {rate.unknown} still unresolved</span>
          )}
        </p>
        <p className="mt-1 text-xs text-ink-400">
          In-flight orders are excluded rather than counted as failures.
        </p>
      </div>
    </Card>
  );
}

function Row({ label, value, to, alarm }: {
  label: string; value: number; to: string; alarm?: boolean;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <Link to={to} className="text-sm text-ink-700 hover:underline">{label}</Link>
      <span className={`tabular text-sm font-semibold ${alarm ? 'text-red-700' : 'text-ink-800'}`}>
        {value}
      </span>
    </div>
  );
}
